import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Select } from '@components';
import { useToast } from '../../../contexts/useToast';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { KnowledgeBaseFormModal } from '../components/KnowledgeBaseFormModal';
import { useKnowledgeBase, useKnowledgeBaseProAccess } from '../hooks';
import {
  KnowledgeBase,
  KnowledgeBaseFormData,
  KnowledgeBaseSettings,
  RECRAWL_INTERVAL_WEEKS,
  RecrawlIntervalWeeks,
} from '../types';

interface SettingsTabProps {
  knowledgeBase: KnowledgeBase;
  store: ReturnType<typeof useKnowledgeBase>;
}

interface SettingSwitchProps {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

const SettingSwitch: React.FC<SettingSwitchProps> = ({
  id,
  label,
  checked,
  disabled,
  onChange,
}) => (
  <label htmlFor={id} className="relative inline-flex items-center cursor-pointer shrink-0">
    <span className="sr-only">{label}</span>
    <input
      id={id}
      type="checkbox"
      className="sr-only peer"
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
    />
    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
  </label>
);

export const SettingsTab: React.FC<SettingsTabProps> = ({ knowledgeBase, store }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const { hasProAccess } = useKnowledgeBaseProAccess();

  const handleEdit = async (data: KnowledgeBaseFormData) => {
    try {
      await store.updateKnowledgeBase(knowledgeBase.id, data);
      showToast({ type: 'success', message: t('knowledgeBase.settings.saved') });
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.settings.saveFailed') });
    }
  };

  const handleSettingsChange = async (settings: Partial<KnowledgeBaseSettings>) => {
    setIsSavingSettings(true);
    try {
      await store.updateSettings(knowledgeBase.id, settings);
      showToast({ type: 'success', message: t('knowledgeBase.settings.saved') });
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.settings.saveFailed') });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleDelete = async () => {
    try {
      await store.deleteKnowledgeBase(knowledgeBase.id);
      showToast({ type: 'success', message: t('knowledgeBase.settings.deleted') });
      navigate('/knowledge-base');
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.settings.deleteFailed') });
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <Card>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('knowledgeBase.settings.general')}
              </h2>
              <p className="font-medium text-gray-900 dark:text-white">{knowledgeBase.name}</p>
              {knowledgeBase.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                  {knowledgeBase.description}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setIsEditOpen(true)}
              className="flex items-center gap-2 shrink-0"
            >
              <span className="material-icons text-base">edit</span>
              {t('knowledgeBase.editKnowledgeBase')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {hasProAccess && (
        <>
          <Card>
            <CardContent className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('knowledgeBase.settings.companyInfoInPrompt')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('knowledgeBase.settings.companyInfoInPromptDescription')}
                </p>
              </div>
              <SettingSwitch
                id="company-info-in-prompt"
                label={t('knowledgeBase.settings.companyInfoInPrompt')}
                checked={knowledgeBase.companyInfoInPrompt}
                disabled={isSavingSettings}
                onChange={checked => handleSettingsChange({ company_info_in_prompt: checked })}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('knowledgeBase.settings.websiteRecrawl')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('knowledgeBase.settings.websiteRecrawlDescription')}
                  </p>
                </div>
                <SettingSwitch
                  id="website-recrawl-enabled"
                  label={t('knowledgeBase.settings.websiteRecrawl')}
                  checked={knowledgeBase.websiteRecrawlEnabled}
                  disabled={isSavingSettings}
                  onChange={checked => handleSettingsChange({ website_recrawl_enabled: checked })}
                />
              </div>
              {knowledgeBase.websiteRecrawlEnabled && (
                <div className="space-y-1">
                  <label
                    htmlFor="website-recrawl-interval"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('knowledgeBase.settings.websiteRecrawlInterval')}
                  </label>
                  <Select
                    id="website-recrawl-interval"
                    value={String(knowledgeBase.websiteRecrawlIntervalWeeks)}
                    disabled={isSavingSettings}
                    onChange={value =>
                      handleSettingsChange({
                        website_recrawl_interval_weeks: Number(
                          Array.isArray(value) ? value[0] : value
                        ) as RecrawlIntervalWeeks,
                      })
                    }
                    options={RECRAWL_INTERVAL_WEEKS.map(weeks => ({
                      value: String(weeks),
                      label: t('knowledgeBase.settings.websiteRecrawlEvery', { count: weeks }),
                    }))}
                    className="w-60 h-10"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('knowledgeBase.settings.unansweredQuestions')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('knowledgeBase.settings.unansweredQuestionsDescription')}
                </p>
              </div>
              <SettingSwitch
                id="unanswered-questions-enabled"
                label={t('knowledgeBase.settings.unansweredQuestions')}
                checked={knowledgeBase.unansweredQuestionsEnabled}
                disabled={isSavingSettings}
                onChange={checked =>
                  handleSettingsChange({ unanswered_questions_enabled: checked })
                }
              />
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardContent className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
              {t('knowledgeBase.deleteKnowledgeBase')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('knowledgeBase.settings.deleteDescription')}
            </p>
          </div>
          <Button variant="danger" onClick={() => setIsDeleteOpen(true)} className="shrink-0">
            {t('knowledgeBase.deleteKnowledgeBase')}
          </Button>
        </CardContent>
      </Card>

      <KnowledgeBaseFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSubmit={handleEdit}
        knowledgeBase={knowledgeBase}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title={t('knowledgeBase.deleteKnowledgeBase')}
        question={t('knowledgeBase.areYouSureDeleteKnowledgeBase')}
        itemName={knowledgeBase.name}
        warning={t('knowledgeBase.thisActionCannotBeUndoneKnowledgeBase')}
        confirmLabel={t('knowledgeBase.deleteKnowledgeBase')}
      />
    </div>
  );
};
